"use client";

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { toast } from "react-toastify";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { avatarFromSeed } from "@/lib/avatar/catalog";
import AvatarBuilder from "./AvatarBuilder";

// La fenêtre où le membre compose son avatar, ouverte depuis le compte comme depuis
// le tableau de bord. Elle dit aussi où l'avatar s'affiche et où la photo garde la main.
export default function AvatarComposeModal({ isOpen, onClose }) {
    const t = useTranslations();
    const { data: session, update } = useSession();
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    // À chaque ouverture, on repart de l'avatar du moment (ou de celui de la graine).
    useEffect(() => {
        if (isOpen) setDraft(session?.user?.avatar || avatarFromSeed(`member-${session?.user?.userId}`));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const save = async () => {
        setSaving(true);
        try {
            const response = await fetch("/api/avatar", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ avatar: draft }),
            });
            if (!response.ok) throw new Error();
            await update();
            toast.success(t("account_avatar_saved"));
            onClose();
        } catch {
            toast.error(t("account_avatar_error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <BottomSheetModal isOpen={isOpen} onClose={onClose} maxWidthClass="sm:max-w-3xl">
            <div className="max-h-[85vh] overflow-y-auto p-6 sm:p-8">
                {draft && <AvatarBuilder value={draft} onChange={setDraft} />}

                {/* Où l'avatar s'affiche, où la photo reste */}
                <div className="mt-6 rounded-2xl bg-blue-50 p-4 text-sm text-blue-900 dark:bg-blue-400/10 dark:text-blue-100">
                    <p className="mb-2 font-semibold">{t("avatar_usage_title")}</p>
                    <ul className="space-y-1.5">
                        <li className="flex items-start gap-2">
                            <SolarIcon name="smile-circle-linear" className="mt-0.5 shrink-0 text-base" />
                            <span>{t("avatar_usage_avatar")}</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <SolarIcon name="camera-linear" className="mt-0.5 shrink-0 text-base" />
                            <span>{t("avatar_usage_photo")}</span>
                        </li>
                    </ul>
                </div>

                <div className="mt-6 flex justify-end">
                    <Button isLoading={saving} progress onClick={save}>
                        {t("account_avatar_save")}
                    </Button>
                </div>
            </div>
        </BottomSheetModal>
    );
}
